import * as React from 'react';

import {
  enumUnit,
  PurchaseLineItemType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { zeroWeight } from '@rematter/modules/trade/utils/weight';
import { TouchButton } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCar } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { InventoryItemsModal } from '../../../_shared/InventoryItemsModal';
import {
  $itemsInterfaceHighlightedMaterialIds,
  $itemsInterfaceHighlightedSectionTitle,
  $itemsInterfaceSuggestedPrices,
  resolveSuggestedPrice,
} from '../_state';
import { NormalizedLineItem } from '../_types';
import {
  EditVehicleDialog,
  VehicleDetailProps,
} from '../dialogs/EditVehicleDialog';
import { useDialogModalStyle } from '../hooks/useDialogModalStyle';

import { $inventoryItemsById } from '~/modules/trade/_state/loadForm.state';
import {
  $closeVehicleModal,
  $setAutoFocusRowId,
  $vehicleModalState,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { useUIPreferences } from '~/shared/hooks';

const ICON_CAR = <LucideCar />;

type Props = {
  onAddItem?: (
    type: PurchaseLineItemType,
    data?: Partial<NormalizedLineItem>,
  ) => void;
  onUpdateItem?: (id: string, updates: Partial<NormalizedLineItem>) => void;
  buttonSize?: 'sm' | 'md' | 'lg' | 'xl';
};

// ⚛️ ⚛️ ⚛️ ADD VEHICLE WIDGET ⚛️ ⚛️ ⚛️
export const AddVehicleWidget: React.FC<Props> = ({
  onAddItem,
  onUpdateItem,
  buttonSize,
}) => {
  // Translations
  const t = useTranslations();

  // States
  const [addModalOpen, setAddModalOpen] = React.useState(false);

  // Atoms
  const highlightedMaterialIds = useAtomValue(
    $itemsInterfaceHighlightedMaterialIds,
  );
  const highlightedSectionTitle = useAtomValue(
    $itemsInterfaceHighlightedSectionTitle,
  );
  const suggestedPrices = useAtomValue($itemsInterfaceSuggestedPrices);
  const vehicleModalState = useAtomValue($vehicleModalState);
  const setAutoFocusRowId = useSetAtom($setAutoFocusRowId);
  const closeVehicleModal = useSetAtom($closeVehicleModal);
  const inventoryItemsById = useAtomValue($inventoryItemsById);

  // Other Hooks
  const { touchModeEnabled } = useUIPreferences();
  const modalStyle = useDialogModalStyle();

  // Callbacks
  const handleAddVehicleItem = React.useCallback(() => {
    if (!onAddItem) return;
    const newItemId = `temp-${uuidv4()}`;
    onAddItem('Vehicle', {
      id: newItemId,
      weights: {
        [enumLoadWeightType.Gross]: zeroWeight(enumUnit.Pounds),
        [enumLoadWeightType.Tare]: zeroWeight(enumUnit.Pounds),
        [enumLoadWeightType.Net]: zeroWeight(enumUnit.Pounds),
      },
      pricingUnit: enumUnit.Pounds,
    });
    setAutoFocusRowId(newItemId);
  }, [onAddItem, setAutoFocusRowId]);

  const handlePress = React.useCallback(() => {
    if (touchModeEnabled) {
      setAddModalOpen(true);
    } else {
      handleAddVehicleItem();
    }
  }, [touchModeEnabled, handleAddVehicleItem]);

  const handleAddModalOpenChange = React.useCallback((isOpen: boolean) => {
    if (!isOpen) setAddModalOpen(false);
  }, []);

  const handleVehicleItemSelect = React.useCallback(
    (materialId?: string) => {
      const lineItemId = vehicleModalState.isOpen
        ? vehicleModalState.lineItemId
        : null;
      const item = materialId ? inventoryItemsById[materialId] : undefined;
      const unit = (item?.effectivePricingUnit as Unit) ?? enumUnit.Pounds;

      if (lineItemId && onUpdateItem) {
        const updates: Partial<NormalizedLineItem> = {
          itemId: materialId ?? null,
        };
        const suggested = resolveSuggestedPrice(suggestedPrices, materialId);
        if (suggested) {
          updates.unitPrice = suggested.unitPrice;
          updates.pricingUnit = suggested.pricingUnit;
        }
        onUpdateItem(lineItemId, updates);
        setAutoFocusRowId(lineItemId);
        return;
      }

      if (!onAddItem) return;

      const newItemId = `temp-${uuidv4()}`;
      const suggested = resolveSuggestedPrice(suggestedPrices, materialId);
      onAddItem('Vehicle', {
        id: newItemId,
        itemId: materialId ?? null,
        weights: {
          [enumLoadWeightType.Gross]: zeroWeight(unit),
          [enumLoadWeightType.Tare]: zeroWeight(unit),
          [enumLoadWeightType.Net]: zeroWeight(unit),
        },
        unitPrice: suggested?.unitPrice ?? null,
        pricingUnit: unit,
      });
      setAutoFocusRowId(newItemId);
    },
    [
      vehicleModalState.isOpen,
      vehicleModalState.lineItemId,
      onUpdateItem,
      onAddItem,
      setAutoFocusRowId,
      inventoryItemsById,
    ],
  );

  const handleUpdateVehicleDetails = React.useCallback(
    (data: VehicleDetailProps) => {
      const yearNum = data.year ? parseInt(data.year, 10) : null;
      onUpdateItem?.(data.id, {
        vehicleVin: data.vin || null,
        vehicleMake: data.make || null,
        vehicleModel: data.model || null,
        vehicleYear: yearNum && !isNaN(yearNum) ? yearNum : null,
      });
    },
    [onUpdateItem],
  );

  const handleVehicleModalOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (!isOpen) closeVehicleModal();
    },
    [closeVehicleModal],
  );

  return (
    <>
      <TouchButton
        onPress={handlePress}
        className="@AddItemButton w-full"
        isSelected={false}
        leftElement={ICON_CAR}
        color="primary"
        variant="outline"
        size={buttonSize}
        aria-label="Add Item"
      >
        {t.scaleTicketPro.actions.addVehicle}
      </TouchButton>

      {/* Add New Inventory Item */}
      <InventoryItemsModal
        isOpen={addModalOpen}
        mode="items"
        onOpenChange={handleAddModalOpenChange}
        onSelectMaterial={handleVehicleItemSelect}
        modalStyle={modalStyle}
        highlightedMaterialIds={highlightedMaterialIds}
        highlightedSectionTitle={highlightedSectionTitle}
      />
      {/* Edit Existing Inventory Item */}
      <InventoryItemsModal
        isOpen={vehicleModalState.isOpen}
        mode="items"
        onOpenChange={handleVehicleModalOpenChange}
        onSelectMaterial={handleVehicleItemSelect}
        modalStyle={modalStyle}
        highlightedMaterialIds={highlightedMaterialIds}
        highlightedSectionTitle={highlightedSectionTitle}
      />
      {/* Edit Vehicle Details */}
      <EditVehicleDialog onUpdateValues={handleUpdateVehicleDetails} />
    </>
  );
};
